import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaExternalLinkAlt, FaFileExcel, FaFilePdf, FaFileWord } from 'react-icons/fa';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { u2000IsApiSameOriginFileUrl } from '../../../u2000Core/tools/u2000IsApiSameOriginFileUrl';
import { NS } from '../../U3204';
import type { U3204ExportFile } from '../../U3204Service';
import {
  type U3204ExportKind,
  u3204ResolveExportKind,
} from '../../utils/u3204ResolveExportKind';

export type { U3204ExportKind };
export { u3204ResolveExportKind };

interface U3204ExportDownloadCardProps {
  exportFile: U3204ExportFile;
}

const U3204_MIME_PDF = 'application/pdf';

function u3204BuildExportFetchHeaders(
  fileUrl: string,
  token: string | null | undefined,
): Record<string, string> {
  // BL-0513 : même règle que le hook auto-download (jamais startsWith(""))
  const isSameOrigin = u2000IsApiSameOriginFileUrl(fileUrl);
  const headers: Record<string, string> = {};
  if (isSameOrigin && token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || '';
  if (apiUrl.includes('ngrok') || window.location.hostname.includes('ngrok')) {
    headers['ngrok-skip-browser-warning'] = '1';
  }
  return headers;
}

async function u3204DownloadExportFile(
  fileUrl: string,
  fileName: string,
  token: string | null | undefined,
): Promise<void> {
  const headers = u3204BuildExportFetchHeaders(fileUrl, token);

  try {
    const response = await fetch(fileUrl, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

async function u3204OpenExportFile(
  fileUrl: string,
  token: string | null | undefined,
): Promise<void> {
  const isSameOrigin = u2000IsApiSameOriginFileUrl(fileUrl);

  if (!isSameOrigin) {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    const response = await fetch(fileUrl, {
      method: 'GET',
      headers: u3204BuildExportFetchHeaders(fileUrl, token),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const pdfBlob =
      blob.type === U3204_MIME_PDF ? blob : new Blob([blob], { type: U3204_MIME_PDF });
    const url = window.URL.createObjectURL(pdfBlob);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }
}

const U3204_EXPORT_KIND_UI: Record<
  U3204ExportKind,
  {
    i18nPrefix: string;
    shellClass: string;
    iconWrapClass: string;
    buttonClass: string;
  }
> = {
  pdf_export: {
    i18nPrefix: 'exportPdf',
    shellClass:
      'flex items-center gap-3 rounded-xl border border-red-200 bg-red-50/80 px-3 py-2.5 text-sm text-neutral-800',
    iconWrapClass:
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white',
    buttonClass:
      'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 shadow-xs hover:bg-red-50 focus:outline-hidden focus:ring-2 focus:ring-red-500/40 disabled:opacity-60',
  },
  word: {
    i18nPrefix: 'exportWord',
    shellClass:
      'flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/80 px-3 py-2.5 text-sm text-neutral-800',
    iconWrapClass:
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white',
    buttonClass:
      'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-800 shadow-xs hover:bg-blue-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500/40 disabled:opacity-60',
  },
  excel: {
    i18nPrefix: 'exportExcel',
    shellClass:
      'flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2.5 text-sm text-neutral-800',
    iconWrapClass:
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white',
    buttonClass:
      'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 shadow-xs hover:bg-emerald-50 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-60',
  },
};

const U3204ExportDownloadCard: React.FC<U3204ExportDownloadCardProps> = ({ exportFile }) => {
  const { t } = useTranslation(NS);
  const { auth } = useU2000AuthContext();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const kind = useMemo(() => u3204ResolveExportKind(exportFile), [exportFile]);
  const ui = U3204_EXPORT_KIND_UI[kind];
  const i18nPrefix = ui.i18nPrefix;

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await u3204DownloadExportFile(exportFile.fileUrl, exportFile.fileName, auth.token);
    } finally {
      setIsDownloading(false);
    }
  }, [auth.token, exportFile.fileName, exportFile.fileUrl, isDownloading]);

  const handleOpen = useCallback(async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      await u3204OpenExportFile(exportFile.fileUrl, auth.token);
    } finally {
      setIsOpening(false);
    }
  }, [auth.token, exportFile.fileUrl, isOpening]);

  return (
    <div
      className={ui.shellClass}
      role="group"
      aria-label={t(`${i18nPrefix}.cardAria`, { ns: NS, fileName: exportFile.fileName })}
    >
      <span className={ui.iconWrapClass} aria-hidden>
        {kind === 'pdf_export' ? (
          <FaFilePdf className="h-4 w-4" />
        ) : kind === 'word' ? (
          <FaFileWord className="h-4 w-4" />
        ) : (
          <FaFileExcel className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{exportFile.fileName}</p>
        <p className="text-xs text-muted-foreground">{t(`${i18nPrefix}.readyHint`, { ns: NS })}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {kind === 'pdf_export' ? (
          <button
            type="button"
            onClick={() => void handleOpen()}
            disabled={isOpening}
            className={ui.buttonClass}
          >
            <FaExternalLinkAlt className="h-3 w-3" aria-hidden />
            {isOpening ? t(`${i18nPrefix}.opening`, { ns: NS }) : t(`${i18nPrefix}.open`, { ns: NS })}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className={ui.buttonClass}
        >
          <FaDownload className="h-3 w-3" aria-hidden />
          {isDownloading
            ? t(`${i18nPrefix}.downloading`, { ns: NS })
            : t(`${i18nPrefix}.download`, { ns: NS })}
        </button>
      </div>
    </div>
  );
};

export default U3204ExportDownloadCard;
